import React, { useState, useEffect } from "react";
import { Alert, Tabs, Spin, Select } from "antd";
import { LoadingOutlined } from "@ant-design/icons";

import SLAViolationOrders from "../../support/components/SLAViolationOrders";
import OrderDetailsModal from "../../support/components/OrderDetailsModal";
import InterventionActionsModal from "../../support/components/InterventionActionsModal";
import {
  showSuccessNotification,
} from "../../../common/utils/notification";
import { _get_support_staffs } from "../../../common/axios_services";
import { WarningOutlined } from "@ant-design/icons";

import useSupportData from "../../support/hooks/useSupportData";

const DriverInterventionStats = ({ stats, loading }) => {
  const waitingToPickUp = stats?.waitingToPickUp?.length || 0;
  const waitingToStart = stats?.waitingToStart?.length || 0;
  const waitingToArrive = stats?.waitingToArrive?.length || 0;

  const statItems = [
    {
      title: "Waiting to Pick Up",
      value: waitingToPickUp,
      icon: <WarningOutlined className="text-orange-600" size={24} />,
      color: "bg-orange-50",
    },
    {
      title: "Waiting to Start",
      value: waitingToStart,
      icon: <WarningOutlined className="text-yellow-600" size={24} />,
      color: "bg-yellow-50",
    },
    {
      title: "Waiting to Arrive",
      value: waitingToArrive,
      icon: <WarningOutlined className="text-red-600" size={24} />,
      color: "bg-red-50",
    },
  ];

  if (loading) {
    return (
      <div className="flex gap-4 overflow-x-auto pb-4 snap-x snap-mandatory scrollbar-hide">
        {[...Array(4)].map((_, index) => (
          <div
            key={index}
            className="bg-white rounded-lg p-5 shadow-sm min-w-[280px] sm:min-w-[320px] snap-start flex-shrink-0 animate-pulse"
          >
            <div className="flex items-start justify-between mb-3">
              <div className="bg-gray-200 p-2 rounded-lg w-12 h-12"></div>
            </div>
            <div className="text-gray-600 mb-1 h-4 bg-gray-200 rounded w-20"></div>
            <div className="text-3xl font-bold text-gray-900 mb-3 h-8 bg-gray-200 rounded w-16"></div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="flex gap-4 overflow-x-auto pb-4 snap-x snap-mandatory scrollbar-hide">
      {statItems.map((stat, index) => (
        <div
          key={index}
          className="bg-white rounded-lg p-5 shadow-sm min-w-[280px] sm:min-w-[320px] snap-start flex-shrink-0"
        >
          <div className="flex items-start justify-between mb-3">
            <div className={`${stat.color} p-2 rounded-lg`}>{stat.icon}</div>
          </div>
          <div className="text-gray-600 mb-1">{stat.title}</div>
          <div className="text-3xl font-bold text-gray-900 mb-3">
            {stat.value.toLocaleString()}
          </div>
        </div>
      ))}
    </div>
  );
};

const DriverInterventionsContent = ({
  useOwnLayout = false,
  userRole = "ADMIN",
}) => {
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderModalVisible, setOrderModalVisible] = useState(false);
  const [interventionModalVisible, setInterventionModalVisible] =
    useState(false);
  const [activeTab, setActiveTab] = useState("waiting-to-pick-up");

  const [selectedSupportManager, setSelectedSupportManager] = useState(null);
  const [selectedSupportOfficer, setSelectedSupportOfficer] = useState(null);
  const [supportManagers, setSupportManagers] = useState([]);
  const [supportOfficers, setSupportOfficers] = useState([]);
  const [loadingFilters, setLoadingFilters] = useState(false);

  const antIcon = <LoadingOutlined style={{ fontSize: 24 }} spin />;

  const { driverSlaViolations, loading, error, refreshData, fetchDriverSlaViolations } =
    useSupportData();

  useEffect(() => {
    const fetchSupportManagers = async () => {
      if (userRole !== "ADMIN" && userRole !== "SE") return;

      setLoadingFilters(true);
      try {
        const response = await _get_support_staffs({
          page: 1,
          limit: 100,
          role: "support_manager",
        });

        const managers =
          response?.data?.data?.data || response?.data?.data || [];
        setSupportManagers(
          managers
            .filter(
              (x) => x.role == "SUPPORT_MANAGER" || x.role == "support_manager",
            )
            .map((sm) => ({
              label:
                `${sm.firstName || ""} ${sm.lastName || ""}`.trim() ||
                sm.email ||
                "Unknown",
              value: sm.id,
            })),
        );
      } catch (error) {
      } finally {
        setLoadingFilters(false);
      }
    };

    fetchSupportManagers();
  }, [userRole]);

  useEffect(() => {
    const fetchSupportOfficers = async () => {
      if (!selectedSupportManager && userRole === "SE") {
        setSupportOfficers([]);
        return;
      }

      if (userRole !== "ADMIN" && userRole !== "SE") return;

      setLoadingFilters(true);
      try {
        const response = await _get_support_staffs({
          page: 1,
          limit: 100,
          role: "support_officer",
        });

        const officers =
          response?.data?.data?.data || response?.data?.data || [];
        setSupportOfficers(
          officers
            .filter(
              (x) => x.role == "SUPPORT_OFFICER" || x.role == "support_officer",
            )
            .map((so) => ({
              label:
                `${so.firstName || ""} ${so.lastName || ""}`.trim() ||
                so.email ||
                "Unknown",
              value: so.id,
            })),
        );
      } catch (error) {
      } finally {
        setLoadingFilters(false);
      }
    };

    fetchSupportOfficers();
  }, [selectedSupportManager, userRole]);

  useEffect(() => {
    if (userRole === "ADMIN" || userRole === "SE") {
      fetchDriverSlaViolations(1, 20, selectedSupportManager, selectedSupportOfficer);
    }
  }, [selectedSupportManager, selectedSupportOfficer, userRole]);

  const handleSMChange = (value) => {
    setSelectedSupportManager(value);
    setSelectedSupportOfficer(null);
  };

  const handleSOChange = (value) => {
    setSelectedSupportOfficer(value);
  };

  const handleClearFilters = () => {
    setSelectedSupportManager(null);
    setSelectedSupportOfficer(null);
  };

  const handleViewOrder = (order) => {
    setSelectedOrder(order);
    setOrderModalVisible(true);
  };

  const handleOpenIntervention = () => {
    setOrderModalVisible(false);
    setInterventionModalVisible(true);
  };

  const getViolationTypeWithActor = () => {
    if (!activeTab || !selectedOrder) return activeTab;
    return activeTab;
  };

  const handleRefresh = async () => {
    await refreshData();
    showSuccessNotification("Dashboard refreshed");
  };

  if (error && !driverSlaViolations) {
    return (
      <Alert
        message="Error Loading Driver Interventions"
        description={error}
        type="error"
        showIcon
      />
    );
  }

  const totalViolations =
    (driverSlaViolations?.waitingToPickUp?.length || 0) +
    (driverSlaViolations?.waitingToStart?.length || 0) +
    (driverSlaViolations?.waitingToArrive?.length || 0);

  const showFilters = userRole === "ADMIN" || userRole === "SE" || userRole === "SM";

  return (
    <Spin spinning={loading || loadingFilters} indicator={antIcon}>
      <div className={useOwnLayout ? "pb-40" : ""}>
        <div className="main_container_title_div mb-5 mt-10">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900">
            Driver Intervention - SLA Dashboard
          </h1>
          <p className="text-gray-600 text-xl sm:text-base">
            Monitor and manage driver SLA violations
          </p>
        </div>

        {showFilters && (
          <div className="mb-4 flex flex-wrap gap-4 items-center bg-gray-50 p-4 rounded">
            <div className="flex-1 min-w-[200px]">
              <label className="block text-sm font-medium mb-1">
                Support Manager
              </label>
              <Select
                placeholder="Select Support Manager"
                value={selectedSupportManager || undefined}
                onChange={handleSMChange}
                allowClear
                onClear={handleClearFilters}
                options={supportManagers}
                style={{ width: "100%" }}
                loading={loadingFilters}
              />
            </div>
          </div>
        )}

        {totalViolations > 0 && (
          <Alert
            message={`${totalViolations} driver orders with SLA violations`}
            description="These orders require immediate driver intervention. Click on violations below to take action."
            type="warning"
            showIcon
            style={{ marginBottom: "20px" }}
          />
        )}

        <div className="mb-8 -mx-4 px-4">
          <DriverInterventionStats stats={driverSlaViolations} loading={loading} />
        </div>

        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          type="card"
          className="support-tabs"
        >
          <Tabs.TabPane
            tab={`Waiting to Pick Up (${driverSlaViolations?.waitingToPickUp?.length || 0})`}
            key="waiting-to-pick-up"
          >
            <div className="mt-6">
              <SLAViolationOrders
                orders={driverSlaViolations?.waitingToPickUp || []}
                violationType="WAITING_TO_PICK_UP"
                loading={loading}
                onViewOrder={handleViewOrder}
                onRefresh={handleRefresh}
              />
            </div>
          </Tabs.TabPane>

          <Tabs.TabPane
            tab={`Waiting to Start (${driverSlaViolations?.waitingToStart?.length || 0})`}
            key="waiting-to-start"
          >
            <div className="mt-6">
              <SLAViolationOrders
                orders={driverSlaViolations?.waitingToStart || []}
                violationType="WAITING_TO_START"
                loading={loading}
                onViewOrder={handleViewOrder}
                onRefresh={handleRefresh}
              />
            </div>
          </Tabs.TabPane>

          <Tabs.TabPane
            tab={`Waiting to Arrive (${driverSlaViolations?.waitingToArrive?.length || 0})`}
            key="waiting-to-arrive"
          >
            <div className="mt-6">
              <SLAViolationOrders
                orders={driverSlaViolations?.waitingToArrive || []}
                violationType="WAITING_TO_ARRIVE"
                loading={loading}
                onViewOrder={handleViewOrder}
                onRefresh={handleRefresh}
              />
            </div>
          </Tabs.TabPane>
        </Tabs>

        <OrderDetailsModal
          visible={orderModalVisible}
          order={selectedOrder}
          onCancel={() => setOrderModalVisible(false)}
          loading={loading}
          tab={activeTab}
          onIntervention={handleOpenIntervention}
        />

        <InterventionActionsModal
          visible={interventionModalVisible}
          order={selectedOrder}
          violationType={getViolationTypeWithActor()}
          onCancel={() => setInterventionModalVisible(false)}
          onSuccess={() => {
            setInterventionModalVisible(false);
            setSelectedOrder(null);
            refreshData();
          }}
        />
      </div>
    </Spin>
  );
};

export default DriverInterventionsContent;
